import React from 'react';
import Modal from '@/Components/Modal';
import { TranslationSummary, TranslationResult } from '@/types';
import { getLanguageName } from '@/utils/textFormatting';
import PrimaryButton from '@/Components/PrimaryButton';
import SecondaryButton from '@/Components/SecondaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faDownload } from '@fortawesome/free-solid-svg-icons';
import { bulkDownloadSubtitles } from '@/services/subtitleService';
import toast from 'react-hot-toast';

type TranslationResultsModalProps = {
    show: boolean;
    onClose: () => void;
    summary: TranslationSummary | null;
};

export default function TranslationResultsModal({
    show,
    onClose,
    summary
}: TranslationResultsModalProps) {
    if (!summary || !show) return null;


    const handleDownloadSuccessful = async () => {
        try {
            const successfulResults = summary.results.filter(r => r.successful.length > 0);
            const jobIds = successfulResults.map(r => r.jobId);
            const languages = Array.from(new Set(successfulResults.flatMap(r => r.successful.map(s => s.language))));

            await bulkDownloadSubtitles(jobIds, languages);
        } catch (error: any) {
            toast.error(error.message || 'Failed to download subtitles');
        }
    };

    const totalJobs = summary.results.length;
    const totalSuccesses = summary.results.reduce((acc, r) => acc + r.successful.length, 0);
    const totalFailures = summary.results.reduce((acc, r) => acc + r.failed.length, 0);

    return (
        <Modal show={show} onClose={onClose} maxWidth="2xl">
            <div className="p-6">
                <div className="flex items-center mb-6">
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Translation Results</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            <span>{totalSuccesses} successful, {totalFailures} failed translations</span> {totalJobs > 1 && (<span>across {totalJobs} {totalJobs === 1 ? 'file' : 'files'}</span>)}
                        </p>
                    </div>
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {summary.results.map((result) => (
                        <ResultItem key={result.jobId} result={result} />
                    ))}
                </div>

                <div className="flex justify-end  mt-6  ">
                    {summary.hasSuccesses && (
                        <PrimaryButton className="me-2" onClick={handleDownloadSuccessful}>
                            <FontAwesomeIcon icon={faDownload} className="mr-2"  />
                            <span>Download Successful</span>
                        </PrimaryButton>
                    )}
                    <SecondaryButton onClick={onClose}>
                        Close
                    </SecondaryButton>
                </div>
            </div>
        </Modal>
    );
}

function ResultItem({ result }: { result: TranslationResult }) {
    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center mb-3">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 flex-1 truncate">{result.jobFileName}</h3>

            </div>

            {result.successful.length > 0 && (
                <div className="mb-2">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">Successful translations</p>
                    <div className="flex flex-wrap gap-1">
                        {result.successful.map((success, idx) => (
                            <span key={idx} className="inline-block px-2 py-1 text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                                {getLanguageName(success.language)}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {result.failed.length > 0 && (
                <div className="mt-4">
                    <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Failed translations</p>
                    <div className="space-y-1">
                        {result.failed.map((failure, idx) => (
                            <div key={idx} className="flex items-start space-x-2 text-sm">
                                <span className="inline-block px-2 py-1 text-xs bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded flex-shrink-0">
                                    {getLanguageName(failure.language)}
                                </span>
                                <span className="text-red-600 dark:text-red-400 text-xs leading-5">{failure.error}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
